
import { Component, effect, EffectRef, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, Signal, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { merge, Observable, OperatorFunction, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { TypeaheadConfig } from '../../interfaces/typeahead.interface';

@Component({
  selector: 'app-typeahead',
  imports: [FormsModule, NgbTypeaheadModule],
  templateUrl: './typeahead.component.html',
  styleUrl: './typeahead.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TypeaheadComponent,
      multi: true
    }
  ]
})
export class TypeaheadComponent<T = any> implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() config!: TypeaheadConfig<T>;
  /** Sets `id` on the inner text input (use this for `<label for="...">`; `id` on `<app-typeahead>` only hits the host, not the input). */
  @Input() inputId?: string;
  /** Sets `name` on the inner input; defaults to `inputId` when omitted (helps forms + autofill audits). */
  @Input() inputName?: string;
  /** NgBootstrap/native autocomplete on the inner input; default `off` for picker-style fields. */
  @Input() autocomplete = 'off';
  /** Draws the same dropdown triangle used by the tag filter. */
  @Input() showDropdownCaret = false;
  @Input() isLoading?: boolean | Signal<boolean>;
  @Output() selectionChange = new EventEmitter<T>();
  @ViewChild('instance') instance!: NgbTypeahead;
  @ViewChild('typeaheadInput') private typeaheadInput?: ElementRef<HTMLInputElement>;

  selectedValue: string = '';
  selectedItem: T | null = null;
  private touched = false; // Track if user has interacted with the field
  private originalValue: string = ''; // Store the original value when user focuses
  private originalItem: T | null = null; // Store the original item when user focuses

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  private onChange = (value: T | null) => {};
  private onTouched = () => {
    this.touched = true;
  };

  searchFunction: OperatorFunction<string, readonly string[]> = this.createSearchFunction();
  private dataEffect?: EffectRef;

  constructor() {
    // Set up effect in constructor (injection context)
    this.dataEffect = effect(() => {
      if (this.config && typeof this.config.data === 'function') {
        // Access the signal to create a dependency
        const data = this.config.data as Signal<T[]>;
        const dataArray = data();
        // Recreate search function when signal changes
        this.searchFunction = this.createSearchFunction();
        console.log('Signal data changed, search function recreated. Data count:', dataArray.length);

        // Check for auto-select
        this.checkForAutoSelect(dataArray);
      }
    });
  }

  private createSearchFunction(): OperatorFunction<string, readonly string[]> {
    return (text$: Observable<string>) => {
      // Convert data to strings using formatter
      const dataAsStrings = this.getData().map(item => this.config.formatter(item));

      // Helper function to filter and limit results
      const filterAndLimit = (searchTerm: string) => {
        if (!searchTerm || searchTerm.length < (this.config.minLength || 0)) {
          return dataAsStrings.slice(0, this.config.maxResults || 10);
        }
        return dataAsStrings
          .filter(item => item.toLowerCase().includes(searchTerm.toLowerCase()))
          .slice(0, this.config.maxResults || 10);
      };

      // Handle typing with debounce
      const inputEvents$ = text$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        map(filterAndLimit)
      );

      // Handle focus and click events (no debounce needed)
      const interactionEvents$ = merge(this.focus$, this.click$).pipe(
        map(filterAndLimit)
      );

      return merge(inputEvents$, interactionEvents$);
    };
  }

  ngOnDestroy(): void {
    if (this.dataEffect) {
      this.dataEffect.destroy();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config) {
      if (this.selectedItem && !this.selectedValue) {
        this.selectedValue = this.config.formatter(this.selectedItem);
      }
      // Recreate the search function when config changes
      this.searchFunction = this.createSearchFunction();
      console.log('Typeahead config changed, search function recreated. Data count:', this.getData().length);

      // Check for auto-select (for non-signal data)
      if (typeof this.config.data !== 'function') {
        const dataArray = this.config.data as T[];
        this.checkForAutoSelect(dataArray);
      }
    }
  }

  private checkForAutoSelect(dataArray: T[]): void {
    // Auto-select if there's only one option and autoSelectSingle is enabled
    if (this.config.autoSelectSingle && dataArray.length === 1 && !this.selectedItem) {
      const singleItem = dataArray[0];
      this.selectedItem = singleItem;
      this.selectedValue = this.config.formatter(singleItem);
      this.onChange(singleItem);
      this.selectionChange.emit(singleItem);
      this.instance?.dismissPopup();
      this.blurInputAfterCommit();
    }
  }

  /** Avoid focus staying on the input after commit; blurring later (e.g. chevron on another control) re-triggers ng-bootstrap typeahead resubscribe and can reopen the popup. */
  private blurInputAfterCommit(): void {
    setTimeout(() => {
      const el = this.typeaheadInput?.nativeElement;
      if (el && document.activeElement === el) {
        el.blur();
      }
    }, 0);
  }

  isInvalid(): boolean {
    if (!this.touched) return false;

    const isRequired = this.config.required || false;
    const hasValue = !!this.selectedItem;
    const hasInvalidText = !!(this.selectedValue && !this.findItemByString(this.selectedValue));

    return (isRequired && !hasValue) || hasInvalidText;
  }

  getValidationMessage(): string {
    if (this.selectedValue && !this.findItemByString(this.selectedValue)) {
      return 'Please select a valid option from the list.';
    }
    if ((this.config.required || false) && !this.selectedItem) {
      return this.config.requiredMessage || 'This field is required and must be chosen from the list.';
    }
    return '';
  }

  private getData(): T[] {
    return typeof this.config.data === 'function' ? this.config.data() : this.config.data;
  }

  private findItemByString(searchString: string): T | null {
    const data = this.getData();
    return data.find(item => this.config.formatter(item) === searchString) || null;
  }

  onFocus(event: any) {
    // Store the current value and item as original before clearing
    this.originalValue = this.selectedValue;
    this.originalItem = this.selectedItem;
    
    // Reset touched state when focusing again - user is actively selecting
    // This prevents validation errors from showing while they're choosing
    this.touched = false;
    
    // Clear the input to give user a clean slate
    this.selectedValue = '';
    this.selectedItem = null;
    
    // Trigger the search function to open dropdown with all options
    // Use a small delay to ensure the value clears first
    setTimeout(() => {
      this.focus$.next('');
      this.click$.next('');
    }, 10);
  }

  onClick(event: any) {
    // Reset touched state when clicking - user is actively selecting
    this.touched = false;
    
    // If input already has a value, clear it and show dropdown
    // This allows users to easily select a different option
    if (this.selectedValue) {
      this.selectedValue = '';
      this.selectedItem = null;
    }
    // Always trigger with empty string to show all options
    this.click$.next('');
  }

  clearSelection(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.instance?.dismissPopup();
    this.selectedItem = null;
    this.selectedValue = '';
    this.originalItem = null;
    this.originalValue = '';
    this.touched = false;
    this.onChange(null);
    this.onTouched();
  }

  onBlur(event: any) {
    this.touched = true;

    // Close immediately so blur handling doesn't race with ng-bootstrap's internal resubscribe
    this.instance?.dismissPopup();

    // If user didn't make a new selection, restore the original value and item
    if (!this.selectedItem && this.originalItem) {
      this.selectedValue = this.originalValue;
      this.selectedItem = this.originalItem;
      this.onChange(this.selectedItem);
    }

    this.onTouched();
  }

  onSelectionChange(value: string) {
    // Only update the display value for manual typing
    // Dropdown selections are handled by onItemSelect
    this.selectedValue = value;
  }

  onItemSelect(item: string) {
    // Only process selection if dropdown is actually open
    // This prevents ghost selections when clicking elsewhere
    if (!this.instance?.isPopupOpen()) {
      return;
    }

    this.instance.dismissPopup();

    this.selectedValue = item;
    this.selectedItem = this.findItemByString(item);
    this.originalValue = ''; // Clear original values since user made a new selection
    this.originalItem = null;
    this.onChange(this.selectedItem);
    if (this.selectedItem) {
      this.selectionChange.emit(this.selectedItem);
    }
    this.onTouched();
    this.blurInputAfterCommit();
  }

  // ControlValueAccessor implementation
  writeValue(value: T | null): void {
    if (value) {
      this.selectedItem = value;
      this.selectedValue = this.config ? this.config.formatter(value) : '';
    } else {
      this.selectedItem = null;
      this.selectedValue = '';
      // Reset touched state when clearing programmatically (e.g., via X button)
      // This prevents validation errors from showing when user intentionally clears
      this.touched = false;
    }
    this.originalValue = ''; // Clear original values when component is updated programmatically
    this.originalItem = null;
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }

  getIsLoading(): boolean {
    if (this.isLoading === undefined) {
      return false;
    }
    return typeof this.isLoading === 'function' ? this.isLoading() : this.isLoading;
  }
}
